/**
 * PRTL-1514: keeps sign-in details out of Sentry breadcrumbs.
 *
 * Fetch/XHR breadcrumbs record request URLs, and tRPC GET queries carry their input in the
 * query string. For auth endpoints the whole query is dropped, along with any body the SDK
 * attached. Everywhere else, query parameters and data keys that name a credential are
 * redacted, and email addresses in breadcrumb messages and string data are masked.
 */

export const REDACTED = "[redacted]";

/** Keys whose values are credentials or identify the person signing in. */
const SENSITIVE_KEY =
    /^(email|password|newpassword|currentpassword|confirmpassword|code|otp|token|temptoken|orgselecttoken|resettoken|refreshtoken)$/i;

/** REST auth endpoints and tRPC procedures that take credentials. */
const AUTH_URL =
    /\/(login|logout|verify-2fa|select-org|forgot-password|reset-password|register|refresh)\b|\/trpc\/[^?]*(auth|login|password|twofactor|2fa|verify)/i;

/**
 * Query parameters that carry a credential or identify someone: reset and invite links
 * (`/reset-password?token=…`), OAuth-style codes, signed URLs. Short and explicit on purpose.
 */
const SENSITIVE_PARAM = /^(token|code|reset|invite|t|key|signature|email)$/i;

const EMAIL = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;

/**
 * A full or app-relative URL with a query string, alone (a navigation `to`) or inside text
 * (an http.client span description, "GET https://api…/info?token=…").
 */
const URL_WITH_QUERY = /(?<=^|\s)(?:https?:\/\/|capacitor:\/\/|\/|#\/)\S*\?\S*/gi;

const BODY_KEYS = ["body", "request_body", "requestBody", "response_body", "responseBody"];

function maskEmails(text: string): string {
    return text.replace(EMAIL, REDACTED);
}

/** Strips or redacts a URL's query string; keeps the path. */
export function scrubUrl(url: string): string {
    const q = url.indexOf("?");
    if (q === -1) return url;
    const base = url.slice(0, q);
    if (AUTH_URL.test(base)) return base;
    const params = new URLSearchParams(url.slice(q + 1));
    // Mask on the decoded values: toString() re-encodes "@" as %40.
    for (const [key, value] of [...params.entries()]) {
        const sensitive = SENSITIVE_PARAM.test(key) || SENSITIVE_KEY.test(key);
        params.set(key, sensitive ? REDACTED : maskEmails(value));
    }
    const query = params.toString();
    return query ? `${base}?${query}` : base;
}

/** Scrubs a string that may be a URL (query params) and masks any email address in it. */
function scrubText(value: string): string {
    return maskEmails(value.replace(URL_WITH_QUERY, (url) => scrubUrl(url)));
}

function scrubValue(value: unknown, depth: number): unknown {
    if (typeof value === "string") return scrubText(value);
    if (depth > 4 || value === null || typeof value !== "object") return value;
    if (Array.isArray(value)) return value.map((v) => scrubValue(v, depth + 1));
    const out: Record<string, unknown> = {};
    for (const [key, v] of Object.entries(value)) {
        out[key] = SENSITIVE_KEY.test(key) ? REDACTED : scrubValue(v, depth + 1);
    }
    return out;
}

/** The parts of a Sentry breadcrumb this scrub reads and rewrites. */
export interface ScrubbableBreadcrumb {
    category?: string;
    message?: string;
    data?: Record<string, unknown>;
}

/** Use as Sentry's `beforeBreadcrumb`: returns a scrubbed copy, never drops the breadcrumb. */
export function scrubBreadcrumb<T extends ScrubbableBreadcrumb>(breadcrumb: T): T {
    const out: T = { ...breadcrumb };
    if (typeof out.message === "string") out.message = scrubText(out.message);
    if (out.data) {
        const data: Record<string, unknown> = { ...out.data };
        const url = typeof data.url === "string" ? data.url : undefined;
        if (url !== undefined) {
            data.url = scrubUrl(url);
            if (AUTH_URL.test(url)) for (const key of BODY_KEYS) delete data[key];
        }
        for (const [key, v] of Object.entries(data)) {
            if (key === "url") continue;
            data[key] = SENSITIVE_KEY.test(key) ? REDACTED : scrubValue(v, 0);
        }
        out.data = data;
    }
    return out;
}

/** The parts of a Sentry event this scrub reads and rewrites. */
export interface ScrubbableEvent {
    transaction?: string;
    spans?: ScrubbableSpan[];
    contexts?: Record<string, unknown>;
    tags?: Record<string, unknown>;
    request?: {
        url?: string;
        query_string?: unknown;
        headers?: Record<string, string>;
    };
}

function scrubQueryString(qs: unknown): unknown {
    if (typeof qs === "string") {
        const scrubbed = scrubUrl(`/?${qs.replace(/^\?/, "")}`);
        const q = scrubbed.indexOf("?");
        return q === -1 ? "" : scrubbed.slice(q + 1);
    }
    if (Array.isArray(qs)) {
        return qs.map((pair) => {
            if (!Array.isArray(pair) || typeof pair[0] !== "string") return pair;
            const [key, value] = pair as [string, unknown];
            if (SENSITIVE_PARAM.test(key) || SENSITIVE_KEY.test(key)) return [key, REDACTED];
            return [key, typeof value === "string" ? maskEmails(value) : value];
        });
    }
    if (qs && typeof qs === "object") return scrubValue(qs, 0);
    return qs;
}

/**
 * Use as Sentry's `beforeSend` (PRTL-1514): the page URL, its query string, the Referer and
 * URL-looking transaction names and tags lose credentials (e.g. a reset link's token).
 */
export function scrubEvent<T extends ScrubbableEvent>(event: T): T {
    const out: T = { ...event };
    if (out.request) {
        const request = { ...out.request };
        if (typeof request.url === "string") request.url = scrubUrl(request.url);
        if (request.query_string !== undefined) {
            request.query_string = scrubQueryString(request.query_string);
        }
        if (request.headers) {
            const headers = { ...request.headers };
            for (const name of Object.keys(headers)) {
                if (name.toLowerCase() === "referer") headers[name] = scrubUrl(headers[name]);
            }
            request.headers = headers;
        }
        out.request = request;
    }
    if (typeof out.transaction === "string") out.transaction = scrubText(out.transaction);
    // Transaction events (beforeSendTransaction): child spans and the root span's trace data.
    if (Array.isArray(out.spans)) out.spans = out.spans.map((span) => scrubSpan(span));
    const trace = out.contexts?.trace;
    if (trace && typeof trace === "object" && !Array.isArray(trace)) {
        const traceCtx = { ...(trace as Record<string, unknown>) };
        if (typeof traceCtx.description === "string") {
            traceCtx.description = scrubText(traceCtx.description);
        }
        const data = traceCtx.data;
        if (data && typeof data === "object" && !Array.isArray(data)) {
            traceCtx.data = scrubSpanData(data as Record<string, unknown>);
        }
        out.contexts = { ...out.contexts, trace: traceCtx };
    }
    if (out.tags) {
        const tags: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(out.tags)) {
            tags[key] = typeof value === "string" ? scrubText(value) : value;
        }
        out.tags = tags;
    }
    return out;
}

/** Span or trace data keys holding a bare query string, e.g. `http.query: "?token=…"`. */
const QUERY_KEY = /(^|\.)query(_string)?$/i;

function scrubSpanData(data: Record<string, unknown>): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(data)) {
        if (SENSITIVE_KEY.test(key)) out[key] = REDACTED;
        else if (QUERY_KEY.test(key) && typeof value === "string") {
            const scrubbed = scrubQueryString(value);
            out[key] = value.startsWith("?") && scrubbed ? `?${String(scrubbed)}` : scrubbed;
        } else out[key] = scrubValue(value, 0);
    }
    return out;
}

/** The parts of a span (static `SpanJSON` or streamed `StreamedSpanJSON`) this scrub rewrites. */
export interface ScrubbableSpan {
    name?: string;
    description?: string;
    data?: Record<string, unknown>;
    attributes?: Record<string, unknown>;
}

/**
 * Use as Sentry's `beforeSendSpan` (PRTL-1514): an `http.client` span to
 * `/reset-password/info?token=…`, or a pageload on a reset link, loses the token from its
 * name/description and from `url`, `http.url`, `http.query` and similar data or attributes.
 */
export function scrubSpan<T extends ScrubbableSpan>(span: T): T {
    const out: T = { ...span };
    if (typeof out.name === "string") out.name = scrubText(out.name);
    if (typeof out.description === "string") out.description = scrubText(out.description);
    if (out.data) out.data = scrubSpanData(out.data);
    if (out.attributes) out.attributes = scrubSpanData(out.attributes);
    return out;
}
